import React, {
    useState, useCallback,
} from 'react';
import {
    Button, Divider, Modal, Tag, Typography, notification,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { PageHeader } from '../components/PageHeader';
import {
    startLoadUser, useAuthStore,
} from '../store/auth.store';
import { getAuthCookies } from '../cookies/auth.cookies';
import { forgotPassword } from '../api/auth.api';
import TwoFactorSetupWizard from '../components/TwoFactor/TwoFactorSetupWizard';

const { Title, Text } = Typography;

const fieldRowStyle: React.CSSProperties = {
    padding: '12px 0',
};

const labelStyle: React.CSSProperties = {
    width: 160,
    flexShrink: 0,
};

const ProfileScreen = () => {
    const {
        t,
    } = useTranslation('shared', { keyPrefix: 'PROFILE' });

    const {
        data: userData,
    } = useAuthStore(
        useShallow((state) => ({ data: state.data })),
    );

    // Password
    const [resetLoading, setResetLoading] = useState(false);

    const handleResetPassword = useCallback(async () => {
        if (!userData?.email) return;
        setResetLoading(true);
        try {
            await forgotPassword({
                email: userData.email,
                role: 'deliveryPartner',
            });
            notification.success({
                message: t('PASSWORD_SENT'),
            });
        } catch (error: any) {
            notification.error({
                message: error?.message || 'Error',
            });
        } finally {
            setResetLoading(false);
        }
    }, [userData?.email, t]);

    // 2FA
    const isTwoFactorEnabled = !!userData?.twoFactorEnabled;
    const [showSetupWizard, setShowSetupWizard] = useState(false);

    const handleSetupComplete = useCallback(async () => {
        setShowSetupWizard(false);
        useAuthStore.setState({
            requires2FASetup: false,
            tempToken: null,
            setupData: null,
            error: null,
        });
        const { token } = getAuthCookies();
        if (token) await startLoadUser(token);
    }, []);

    const handleSetupCancel = useCallback(() => {
        setShowSetupWizard(false);
        useAuthStore.setState({
            requires2FASetup: false,
            tempToken: null,
            setupData: null,
            error: null,
        });
    }, []);

    const fullName = [userData?.name, userData?.lastName].filter(Boolean).join(' ');
    const phone = userData?.fullMobile
        || `${userData?.countryCode || ''} ${userData?.mobile || ''}`.trim();

    return (
        <div className="screen">
            <PageHeader title={t('TITLE')} style={{ padding: 0 }} />

            <div className="section">
                {/* Datos personales */}
                <section>
                    <Title level={5} style={{ margin: 0, marginBottom: 4 }}>
                        {t('PERSONAL_DATA')}
                    </Title>

                    <div style={fieldRowStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                            <Text type="secondary" style={labelStyle}>{t('NAME')}</Text>
                            <Text strong>{fullName || '—'}</Text>
                        </div>
                    </div>
                    <div style={fieldRowStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                            <Text type="secondary" style={labelStyle}>{t('EMAIL')}</Text>
                            <Text strong>{userData?.email || '—'}</Text>
                        </div>
                    </div>
                    <div style={fieldRowStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                            <Text type="secondary" style={labelStyle}>{t('PHONE')}</Text>
                            <Text strong>{phone || '—'}</Text>
                        </div>
                    </div>
                </section>

                <Divider />

                {/* Contraseña */}
                <section>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                    >
                        <div>
                            <Title level={5} style={{ margin: 0 }}>
                                {t('PASSWORD_TITLE')}
                            </Title>
                            <Text type="secondary" style={{ fontSize: 13 }}>
                                {t('PASSWORD_DESCRIPTION')}
                            </Text>
                        </div>
                        <Button
                            onClick={handleResetPassword}
                            loading={resetLoading}
                        >
                            {t('PASSWORD_RESET')}
                        </Button>
                    </div>
                </section>

                <Divider />

                {/* Verificación en dos pasos */}
                <section>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                    }}
                    >
                        <div>
                            <Title level={5} style={{ margin: 0, marginBottom: 4 }}>
                                {t('TWO_FACTOR_TITLE')}
                            </Title>
                            <Text type="secondary" style={{ fontSize: 13 }}>
                                {t('TWO_FACTOR_DESCRIPTION')}
                            </Text>
                        </div>
                        {isTwoFactorEnabled ? (
                            <Tag color="success" style={{ color: '#135200' }}>{t('STATUS_ACTIVE')}</Tag>
                        ) : (
                            <Button
                                type="primary"
                                onClick={() => {
                                    useAuthStore.setState({
                                        tempToken: null,
                                        error: null,
                                        setupData: null,
                                    });
                                    setShowSetupWizard(true);
                                }}
                            >
                                {t('ACTIVATE')}
                            </Button>
                        )}
                    </div>
                </section>

                <Modal
                    open={showSetupWizard}
                    onCancel={handleSetupCancel}
                    footer={null}
                    destroyOnHidden
                    centered
                    width={560}
                >
                    <Title level={5}>{t('SETUP_MODAL_TITLE')}</Title>
                    <TwoFactorSetupWizard
                        onComplete={handleSetupComplete}
                        onSkip={handleSetupCancel}
                        embedded
                    />
                </Modal>
            </div>
        </div>
    );
};

export { ProfileScreen };
